import React, { useState, useEffect, useCallback } from 'react';
import {
  getContractors,
  createContractor,
  updateContractor,
  getContractorStats
} from '../Config/contractorService';
import { startUserVerification } from '../Config/verificationService';
import Topbar from '../Components/Topbar';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';

function Contractor() {
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedContractor, setSelectedContractor] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    email: '',
    gender: '',
    profile_photo: '',
    age: '',
    active: true,
    commission_percentage: ''
  });
  const [filters, setFilters] = useState({
    name: '',
    email: '',
    mobile: '',
    active: '',
    verification_status: ''
  });
  const [activeStatCard, setActiveStatCard] = useState(null);
  const [stats, setStats] = useState({ total: 0, verified: 0, pending: 0, inactive: 0 });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1
  });
  const navigate = useNavigate();

  const fetchStats = async () => {
    try {
      const result = await getContractorStats();
      setStats(result);
    } catch (err) {
      console.error('Failed to fetch contractor stats:', err);
    }
  };

  const fetchContractors = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getContractors({
        ...filters,
        page: pagination.page,
        limit: pagination.limit
      });
      if (response.status === 'success') {
        const contractorRows = Array.isArray(response.data) ? response.data : [];
        setContractors(contractorRows);
        setPagination(prev => {
          const total = response.pagination?.total ?? response.meta?.total ?? contractorRows.length;
          const totalPagesFromResponse = response.pagination?.totalPages
            ?? response.pagination?.pages
            ?? response.meta?.totalPages
            ?? response.meta?.pages;
          const computedTotalPages = totalPagesFromResponse ?? Math.max(1, Math.ceil(total / prev.limit));

          return {
            ...prev,
            total,
            totalPages: computedTotalPages
          };
        });
      } else {
        setError('Failed to fetch contractors');
        toast.error('Failed to fetch contractors');
      }
    } catch (error) {
      setError('Failed to fetch contractors');
      toast.error('Failed to fetch contractors');
    }
    setLoading(false);
  }, [filters, pagination.page, pagination.limit]);

  useEffect(() => {
    fetchContractors();
  }, [fetchContractors]);

  useEffect(() => {
    fetchStats();
  }, []);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleClearFilters = () => {
    setFilters({
      name: '',
      email: '',
      mobile: '',
      active: '',
      verification_status: ''
    });
    setActiveStatCard(null);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleStatCardClick = (cardKey) => {
    if (activeStatCard === cardKey) {
      // Toggle off — reset to all
      setActiveStatCard(null);
      setFilters(prev => ({ ...prev, active: '', verification_status: '' }));
      setPagination(prev => ({ ...prev, page: 1 }));
    } else {
      setActiveStatCard(cardKey);
      if (cardKey === 'total') {
        setFilters(prev => ({ ...prev, active: '', verification_status: '' }));
      } else if (cardKey === 'inactive') {
        setFilters(prev => ({ ...prev, active: 'false', verification_status: '' }));
      } else if (cardKey === 'verified') {
        setFilters(prev => ({ ...prev, active: '', verification_status: 'VERIFIED' }));
      } else if (cardKey === 'pending') {
        setFilters(prev => ({ ...prev, active: '', verification_status: 'PENDING' }));
      }
      setPagination(prev => ({ ...prev, page: 1 }));
    }
  };

  const displayedContractors = contractors;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedContractor) {
        const response = await updateContractor(selectedContractor.contractor_id, formData);
        if (response.status === 'success') {
          toast.success('Contractor updated successfully');
          setShowModal(false);
          fetchContractors();
        } else {
          toast.error('Failed to update contractor');
        }
      } else {
        const response = await createContractor(formData);
        if (response.status === 'success') {
          toast.success('Contractor created successfully');
          setShowModal(false);
          fetchContractors();
        } else {
          toast.error('Failed to create contractor');
        }
      }
    } catch (error) {
      toast.error('An error occurred');
    }
  };

  const handleEdit = (contractor) => {
    setSelectedContractor(contractor);
    setFormData({
      name: contractor.user.name,
      mobile: contractor.user.mobile,
      email: contractor.user.email,
      gender: contractor.user.gender,
      profile_photo: contractor.user.profile_photo,
      age: contractor.user.age,
      active: contractor.active,
      commission_percentage: contractor.commission_percentage
    });
    setShowModal(true);
  };

  const handleViewDetails = (contractor) => {
    navigate(`/contractors/${contractor.contractor_id}`);
  };

  const handleStartVerification = async (contractor) => {
    try {
      const response = await startUserVerification(contractor.user_id, 'CONTRACTOR');
      if (response.success) {
        toast.success('Contractor verification started successfully');
        fetchContractors(); // Refresh the list
      } else {
        toast.error('Failed to start verification');
      }
    } catch (error) {
      toast.error('An error occurred while starting verification');
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <Topbar />
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Contractors</h1>
          <button
            onClick={() => {
              setSelectedContractor(null);
              setFormData({
                name: '',
                mobile: '',
                email: '',
                gender: '',
                profile_photo: '',
                age: '',
                active: true,
                commission_percentage: ''
              });
              setShowModal(true);
            }}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
          >
            Add Contractor
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <button
            type="button"
            onClick={() => handleStatCardClick('total')}
            aria-pressed={activeStatCard === 'total'}
            className={`bg-white p-4 rounded-lg shadow text-left transition hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[#EF5E28] ${activeStatCard === 'total' ? 'ring-2 ring-[#EF5E28]' : ''}`}
          >
            <h3 className="text-sm font-medium text-gray-500">Total</h3>
            <p className="text-2xl font-bold text-gray-800">{stats.total}</p>
          </button>
          <button
            type="button"
            onClick={() => handleStatCardClick('verified')}
            aria-pressed={activeStatCard === 'verified'}
            className={`bg-white p-4 rounded-lg shadow text-left transition hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[#EF5E28] ${activeStatCard === 'verified' ? 'ring-2 ring-[#EF5E28]' : ''}`}
          >
            <h3 className="text-sm font-medium text-gray-500">Verified</h3>
            <p className="text-2xl font-bold text-green-600">{stats.verified}</p>
          </button>
          <button
            type="button"
            onClick={() => handleStatCardClick('pending')}
            aria-pressed={activeStatCard === 'pending'}
            className={`bg-white p-4 rounded-lg shadow text-left transition hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[#EF5E28] ${activeStatCard === 'pending' ? 'ring-2 ring-[#EF5E28]' : ''}`}
          >
            <h3 className="text-sm font-medium text-gray-500">Pending</h3>
            <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
          </button>
          <button
            type="button"
            onClick={() => handleStatCardClick('inactive')}
            aria-pressed={activeStatCard === 'inactive'}
            className={`bg-white p-4 rounded-lg shadow text-left transition hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[#EF5E28] ${activeStatCard === 'inactive' ? 'ring-2 ring-[#EF5E28]' : ''}`}
          >
            <h3 className="text-sm font-medium text-gray-500">Inactive</h3>
            <p className="text-2xl font-bold text-red-600">{stats.inactive}</p>
          </button>
        </div>

        {/* Filters Section */}
        <div className="bg-white p-4 rounded-lg shadow mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Name</label>
              <input
                type="text"
                name="name"
                value={filters.name}
                onChange={handleFilterChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder="Search by name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Email</label>
              <input
                type="text"
                name="email"
                value={filters.email}
                onChange={handleFilterChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder="Search by email"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Mobile</label>
              <input
                type="text"
                name="mobile"
                value={filters.mobile}
                onChange={handleFilterChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder="Search by mobile"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Status</label>
              <select
                name="active"
                value={filters.active}
                onChange={handleFilterChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="">All</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <button
              onClick={handleClearFilters}
              className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
            >
              Clear Filters
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-center">Loading...</div>
        ) : error ? (
          <div className="text-red-500 text-center">{error}</div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mobile</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Verification</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Commission</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {displayedContractors.map((contractor) => (
                  <tr key={contractor.contractor_id}>
                    <td className="px-6 py-4 whitespace-nowrap">{contractor.user.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{contractor.user.mobile}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{contractor.user.email}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${contractor.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {contractor.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        contractor.verification_status === 'VERIFIED' ? 'bg-green-100 text-green-800' :
                        contractor.verification_status === 'REJECTED' ? 'bg-red-100 text-red-800' :
                        contractor.verification_status === 'SUSPENDED' ? 'bg-orange-100 text-orange-800' :
                        contractor.verification_status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                        contractor.verification_status === 'NOT_STARTED' ? 'bg-gray-100 text-gray-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {contractor.verification_status || 'NOT_STARTED'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">{contractor.commission_percentage}%</td>
                    <td className="px-6 py-4 whitespace-nowrap text-left">
                      <button
                        onClick={() => handleViewDetails(contractor)}
                        className="text-blue-500 hover:text-blue-600 hover:bg-blue-100 px-3 py-1 rounded mr-3"
                        title="View Details"
                      >
                        View
                      </button>
                      <button 
                        onClick={() => handleEdit(contractor)}
                        className="text-yellow-500 hover:text-yellow-600 hover:bg-yellow-100 px-3 py-1 rounded"
                        title="Edit"
                      >
                        Edit
                      </button>
                      {(contractor.verification_status === 'NOT_STARTED' || !contractor.verification_status || contractor.verification_status === 'REJECTED') && (
                        <button
                          onClick={() => handleStartVerification(contractor)}
                          className="bg-green-500 text-white px-3 py-1 rounded hover:bg-green-600"
                        >
                          Start Verification
                        </button>
                      )}
                      {contractor.verification_status === 'PENDING' && (
                        <span className="text-blue-600 text-sm font-medium">
                          Verification In Progress
                        </span>
                      )}
                      {contractor.verification_status === 'VERIFIED' && (
                        <span className="text-green-600 text-sm font-medium">
                          ✓ Verified
                        </span>
                      )}
                      {contractor.verification_status === 'SUSPENDED' && (
                        <span className="text-orange-600 text-sm font-medium">
                          ⚠ Suspended
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="px-6 py-4 border-t flex items-center justify-between">
              <span className="text-sm text-gray-600">
                Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                  disabled={pagination.page <= 1}
                  className="px-3 py-1 border rounded disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() =>
                    setPagination(prev => ({
                      ...prev,
                      page: Math.min(prev.totalPages, prev.page + 1)
                    }))
                  }
                  disabled={pagination.page >= pagination.totalPages}
                  className="px-3 py-1 border rounded disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}

        {showModal && (
          <div className="fixed inset-0 z-[120] bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full p-4">
            <div className="relative top-0 sm:top-20 mx-auto my-4 sm:my-8 p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  {selectedContractor ? 'Edit Contractor' : 'Add Contractor'}
                </h3>
                <form onSubmit={handleSubmit} className="mt-4">
                  <div className="mb-4">
                    <label className="block text-gray-700 text-sm font-bold mb-2">Name</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                      required
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-gray-700 text-sm font-bold mb-2">Mobile</label>
                    <input
                      type="text"
                      name="mobile"
                      value={formData.mobile}
                      onChange={handleInputChange}
                      className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                      required
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-gray-700 text-sm font-bold mb-2">Email</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-gray-700 text-sm font-bold mb-2">Gender</label>
                    <select
                      name="gender"
                      value={formData.gender}
                      onChange={handleInputChange}
                      className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                    >
                      <option value="">Select Gender</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div className="mb-4">
                    <label className="block text-gray-700 text-sm font-bold mb-2">Age</label>
                    <input
                      type="number"
                      name="age"
                      value={formData.age}
                      onChange={handleInputChange}
                      className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-gray-700 text-sm font-bold mb-2">Profile Photo URL</label>
                    <input
                      type="text"
                      name="profile_photo"
                      value={formData.profile_photo}
                      onChange={handleInputChange}
                      className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-gray-700 text-sm font-bold mb-2">Status</label>
                    <select
                      name="active"
                      value={formData.active}
                      onChange={handleInputChange}
                      className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                    >
                      <option value={true}>Active</option>
                      <option value={false}>Inactive</option>
                    </select>
                  </div>
                  <div className="mb-4">
                    <label className="block text-gray-700 text-sm font-bold mb-2">Commission Percentage</label>
                    <input
                      type="number"
                      name="commission_percentage"
                      value={formData.commission_percentage}
                      onChange={handleInputChange}
                      className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                      required
                    />
                  </div>
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => setShowModal(false)}
                      className="bg-gray-300 text-gray-700 px-4 py-2 rounded mr-2 hover:bg-gray-400"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                    >
                      {selectedContractor ? 'Update' : 'Create'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        <ToastContainer />
      </div>
    </div>
  );
}

export default Contractor; 